import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { invoke } from '@tauri-apps/api/core';
import { Modal } from './ui/Modal';
import VaultPassphraseInput, { VaultPassphraseStrength } from './VaultPassphraseInput';
import VaultRecoveryPhraseStep from './VaultRecoveryPhraseStep';
import {
  MIN_PASSPHRASE_CHARS,
  nfc,
  passphraseProblem,
  pwStrength,
} from './vaultPassphraseUtils';
import { vaultErrorToken, refreshVaultMaster } from '../store/vaultStore';
import { accountEmail } from './vaultAccountEmail';
import './Vault.css';

/**
 * First-time setup (CONTRACT §6.2), the migration of an existing vault
 * (§6.5), and the bootstrap ceremony for a genuinely fresh scope
 * (vault-storage-transition TSW3-MIGRATION.md §3.4b/§12 Step 1) — three
 * framings of the same two steps, because the mechanics only differ in which
 * pair of commands prepares/commits: `vault_master_prepare`/`_commit` for
 * setup and migrate (both require this scope to already be unlocked —
 * `require_dek`), `vault_bootstrap_prepare`/`_commit` for bootstrap (which
 * derives a brand-new DEK from scratch and needs no prior unlock at all).
 * The screen never asks the caller to know the difference: `VaultPassphraseGateHost`
 * picks the mode from `vault_status`/`vault_master_status` alone.
 *
 * Two-phase on purpose. A one-shot "set the passphrase" can crash after the
 * recovery wrap is durable but before the words reached the user — a recovery
 * path they do not possess and do not know they lack. Here, nothing is durable
 * until `Done`: cancelling on either step leaves the vault exactly as it was.
 *
 * **Bootstrap's extra step, invisible to this component's own copy.**
 * `vault_bootstrap_commit` — unlike `vault_master_commit` — never touches the
 * in-memory key (`vault_local::master_bootstrap_commit` only `INSERT`s the
 * row; there is no pre-existing unlock for it to have inherited from, unlike
 * setup/migrate). Left there, the ceremony would "succeed" into a vault that
 * is still locked, immediately re-asking for the very passphrase just
 * confirmed. `handleCommit` unlocks explicitly, with the same passphrase, the
 * moment bootstrap commits and before it is cleared from state.
 *
 * The passphrase lives in component state for the life of the modal and is
 * dropped on close. It is never logged, never put in an error string and never
 * written anywhere. `passphrase_weak` comes back from Rust without a reason
 * attached, so no screen here can become a strength oracle.
 */
/** Gate reasons that come from saving or using a secret (git token, DB password). */
const SECRET_REASONS = new Set(['git-account', 'git-sync', 'secure-storage', 'db-manager']);

export default function VaultPassphraseSetupModal({
  isOpen,
  /** 'setup' (empty vault, already unlocked) | 'migrate' (existing items,
   *  already unlocked) | 'bootstrap' (genuinely fresh scope, not yet unlocked
   *  — vault_bootstrap_prepare/_commit instead of vault_master_prepare/_commit) */
  mode = 'setup',
  itemCount = 0,
  /** Why the gate opened (`openVaultGate(reason)`); a secret save explains the Vault. */
  reason = 'vault',
  onClose,
  onDone,
}) {
  const { t } = useTranslation();
  const migrating = mode === 'migrate';
  const bootstrapping = mode === 'bootstrap';
  // A git token or DB password asked for a Vault that does not exist yet
  // (they used to go to the OS keychain): say why one is being created.
  const forSecret = SECRET_REASONS.has(reason);

  const [step, setStep] = useState(1);
  const [passphrase, setPassphrase] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // { handle, words, kdfMs } from vault_master_prepare. Nothing durable yet.
  const [prepared, setPrepared] = useState(null);
  // True once `vault_bootstrap_commit` itself has returned successfully —
  // i.e. the passphrase is now durably written to `vault_meta` — even if the
  // follow-up `vault_unlock_with_secret` below then fails (phase 7 review,
  // minor finding). Distinct from `prepared`/`step`: a plain "Start over"
  // after this point would re-run `vault_bootstrap_prepare`, which now
  // refuses every attempt with `master_already_set`
  // (`ensure_bootstrap_eligible`) since a real passphrase already governs
  // this scope — a dead end with no unlock affordance on screen. Once this
  // is true, retrying means re-trying the unlock, never re-preparing.
  const [bootstrapCommitted, setBootstrapCommitted] = useState(false);

  const reset = useCallback(() => {
    setStep(1);
    setPassphrase('');
    setConfirm('');
    setBusy(false);
    setError('');
    setPrepared(null);
    setBootstrapCommitted(false);
  }, []);

  useEffect(() => {
    if (isOpen) reset();
  }, [isOpen, reset]);

  // A parked setup holds a copy of the vault key for its 10-minute TTL (or,
  // for bootstrap, a freshly-generated one — same TTL, same map). Closing the
  // modal drops it immediately rather than waiting for the sweep.
  // `vault_master_cancel` is generic over the parked handle regardless of
  // which ceremony parked it (`VaultState.pending_master` is one map, keyed
  // by handle, not two) — no separate `vault_bootstrap_cancel` exists or is
  // needed.
  const cancelPrepared = useCallback((handle) => {
    if (!handle) return;
    invoke('vault_master_cancel', { handle }).catch(() => {});
  }, []);

  const problem = passphraseProblem(passphrase, confirm);
  const showTooShort = passphrase !== '' && [...nfc(passphrase)].length < MIN_PASSPHRASE_CHARS;
  const showMismatch = confirm !== '' && problem === 'mismatch';
  const showEdgeSpace = problem === 'edgeSpace';
  const showRepetitive = problem === 'tooRepetitive';
  // Advisory (§5): never disables the button, never claims to have measured
  // guessability — `pwStrength` counts character classes and nothing more.
  const showWeak = !showTooShort && passphrase !== '' && pwStrength(nfc(passphrase)) <= 2;

  const errorText = (e) => {
    switch (vaultErrorToken(e)) {
      case 'passphrase_weak':
        return t('vault.errWeak', 'That passphrase is too common. Choose something else.');
      case 'passphrase_mismatch':
        return t('vault.errMismatch', 'Both entries must match.');
      case 'master_already_set':
        return t('vault.errAlreadySet', 'This vault already has a passphrase.');
      case 'vault_needs_upgrade':
        return t(
          'vault.errNeedsUpgrade',
          'This vault was written by a newer version of Apiboo. Update Apiboo to open it.'
        );
      case 'handle_expired':
        return t('vault.errHandleExpired', 'This took too long. Start again to get a new phrase.');
      case 'vault_key_mismatch':
        // Rust refused to certify the key this session is holding, because it
        // does not decrypt this vault's items. Setting a passphrase around it
        // would make that permanent, so the honest answer is "not from here".
        return t(
          'vault.errKeyMismatch',
          'Apiboo cannot read this vault with the key on this computer, so it will not lock it to a passphrase. Restore your vault from a backup first.'
        );
      default:
        return t('vault.errGeneric', 'That did not work. Try again.');
    }
  };

  const handlePrepare = async () => {
    if (busy || problem) return;
    setBusy(true);
    setError('');
    try {
      // Same shape either way (`{ handle, words, kdfMs }`) — only which
      // ceremony parks it differs (module doc comment).
      const result = await invoke(
        bootstrapping ? 'vault_bootstrap_prepare' : 'vault_master_prepare',
        { passphrase, confirm, accountEmail: accountEmail() }
      );
      setPrepared(result);
      setStep(2);
    } catch (e) {
      setError(errorText(e));
    }
    setBusy(false);
  };

  // Shared tail of a successful ceremony (commit — and for bootstrap, the
  // follow-up unlock — both done). Split out so `retryUnlock` below can reach
  // the same finish line without repeating it.
  const finishUnlocked = useCallback(async () => {
    setPassphrase('');
    setConfirm('');
    await refreshVaultMaster();
    if (onDone) onDone();
    // After `onDone`, not before: the parent unmounts this modal, and
    // clearing `prepared` first would paint one frame of an empty word grid
    // on the way out. The words go out of scope with the component either
    // way — this setState is a no-op once it has unmounted.
    setPrepared(null);
  }, [onDone]);

  const handleCommit = async () => {
    if (busy || !prepared?.handle) return;
    setBusy(true);
    setError('');
    try {
      if (bootstrapping) {
        // `vault_bootstrap_commit` returns `()`, not a `VaultStatus` — unlike
        // `vault_master_commit`, it never touches the in-memory key (module
        // doc comment). Unlock explicitly with the passphrase just confirmed,
        // before it is cleared below, so the ceremony actually leaves the
        // vault open rather than durable-but-still-locked.
        await invoke('vault_bootstrap_commit', { handle: prepared.handle });
        // The passphrase is now durably set (`master_bootstrap_commit` writes
        // and verifies inside its own transaction) — from this point on a
        // failure below is an unlock failure, not a setup failure, and must
        // not send "Start over" back to `vault_bootstrap_prepare` (phase 7
        // review, minor finding): that call would now refuse with
        // `master_already_set` since a real passphrase already governs this
        // scope, leaving no unlock affordance on screen at all.
        setBootstrapCommitted(true);
        await invoke('vault_unlock_with_secret', { secret: passphrase, remember: false });
      } else {
        // No device cache is offered, so setup never asks and never enables it.
        await invoke('vault_master_commit', { handle: prepared.handle, remember: false });
      }
      await finishUnlocked();
    } catch (e) {
      setError(errorText(e));
      setBusy(false);
    }
  };

  // Retries only `vault_unlock_with_secret`, never re-prepares — the correct
  // recovery once `bootstrapCommitted` is true (see its own comment above).
  // `passphrase` is still in state: the failure path never clears it.
  const retryUnlock = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await invoke('vault_unlock_with_secret', { secret: passphrase, remember: false });
      await finishUnlocked();
    } catch (e) {
      setError(errorText(e));
      setBusy(false);
    }
  };

  const handleClose = () => {
    if (busy) return;
    cancelPrepared(prepared?.handle);
    if (onClose) onClose();
  };

  // Step 2 is undismissable: backdrop and Escape are disabled and the close
  // button is hidden by `.vault-pass-modal--nodismiss` (§6.2). `Start over` is
  // the only exit, and it exists so an expired handle cannot trap anyone —
  // going back discards a phrase that was never committed, which is safe.
  const nodismiss = step === 2;

  return (
    <Modal
      isOpen={isOpen}
      onClose={nodismiss ? () => {} : handleClose}
      preventBackdropClose={nodismiss}
      preventEscapeClose={nodismiss}
      size="xl"
      contentClassName={nodismiss ? 'vault-pass-modal--nodismiss' : ''}
      title={
        step === 2
          ? t('vault.recoveryTitle', 'Save your recovery phrase')
          : migrating
            ? t('vault.migrateTitle', 'Protect this vault with a passphrase')
            : t('vault.setupTitle', 'Create your vault passphrase')
      }
    >
      {step === 1 ? (
        <div className="vault-pass-body">
          {migrating ? (
            <p className="vault-pass-text">
              {t(
                'vault.migrateIntro',
                'Your {{count}} items stay exactly as they are. Nothing is re-encrypted and nothing is uploaded — Apiboo only adds a lock that your passphrase opens.',
                { count: itemCount }
              )}
            </p>
          ) : (
            <>
              {forSecret && (
                <p className="vault-pass-text" data-testid="vault-setup-why-secrets">
                  {t(
                    'vault.setupWhySecrets',
                    'Apiboo keeps git tokens and database passwords in your encrypted Vault, not in the system keychain. Create the Vault once and we’ll save it right after.'
                  )}
                </p>
              )}
              <p className="vault-pass-text">
                {t(
                  'vault.setupIntro',
                  'Your vault is encrypted with this passphrase. Apiboo does not store it, cannot see it, and cannot reset it.'
                )}
              </p>
            </>
          )}

          {/* H9 — required on both flows. */}
          <p className="vault-pass-text">
            <strong>
              {t(
                'vault.setupNotAccountPassword',
                'This is not your Apiboo account password. It is a separate passphrase for this vault.'
              )}
            </strong>
          </p>

          <VaultPassphraseInput
            id="vault-passphrase-new"
            label={t('vault.setupField1', 'Passphrase (at least 12 characters)')}
            value={passphrase}
            onChange={setPassphrase}
            autoComplete="new-password"
            autoFocus
            disabled={busy}
          />
          <VaultPassphraseStrength value={passphrase} />
          <p className="vault-pass-hint">
            {t(
              'vault.setupFieldHint',
              'Four or more unrelated words make a passphrase that is both strong and possible to remember.'
            )}
          </p>

          <VaultPassphraseInput
            id="vault-passphrase-confirm"
            label={t('vault.setupField2', 'Confirm passphrase')}
            value={confirm}
            onChange={setConfirm}
            autoComplete="new-password"
            disabled={busy}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handlePrepare();
            }}
          />

          {showTooShort && (
            <span className="vault-modal-error">
              {t('vault.errTooShort', 'Use at least 12 characters.')}
            </span>
          )}
          {showRepetitive && (
            <span className="vault-modal-error">
              {t('vault.errRepetitive', 'Use at least 5 different characters — a repeated pattern is the first thing an attacker tries.')}
            </span>
          )}
          {showEdgeSpace && (
            <span className="vault-modal-error">
              {t(
                'vault.errEdgeSpace',
                'Remove the space at the start or end — a space you cannot see is a passphrase you cannot retype.'
              )}
            </span>
          )}
          {showMismatch && (
            <span className="vault-modal-error">
              {t('vault.errMismatch', 'Both entries must match.')}
            </span>
          )}
          {showWeak && (
            <p className="vault-pass-hint">
              {t(
                'vault.setupWeakAdvisory',
                'This passphrase is weak. Anyone who copies your vault file can guess at it offline, as fast as their hardware allows.'
              )}
            </p>
          )}

          {/* Always visible, never behind a disclosure. */}
          <div className="vault-pass-warn">
            <span className="vault-pass-warn-title">
              {t('vault.noResetTitle', 'There is no reset and no recovery.')}
            </span>
            <span className="vault-pass-warn-text">
              {t(
                'vault.noResetBody',
                'If you lose this passphrase and the recovery phrase on the next screen, everything in this vault is gone permanently — passwords, SFTP keys, notes and every passkey. A lost passkey can lock you out of that website for good.'
              )}
            </span>
          </div>

          {error && <div className="vault-modal-error">{error}</div>}

          <div className="modal-footer-row">
            <button
              type="button"
              onClick={handleClose}
              disabled={busy}
              className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handlePrepare}
              disabled={busy || !!problem}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors modern-button text-sm"
            >
              {busy
                ? migrating
                  ? t('vault.migrateBusy', 'Protecting…')
                  : t('vault.setupBusy', 'Encrypting…')
                : migrating
                  ? t('vault.migratePrimary', 'Set passphrase')
                  : t('vault.setupPrimary', 'Create vault')}
            </button>
          </div>
        </div>
      ) : (
        <VaultRecoveryPhraseStep
          words={prepared?.words || []}
          busy={busy}
          error={error}
          onDone={handleCommit}
        />
      )}
      {step === 2 && error && (
        <div className="vault-pass-body vault-pass-startover">
          <button
            type="button"
            className="vault-pass-linkbtn"
            disabled={busy}
            onClick={
              bootstrapCommitted
                ? retryUnlock
                : () => {
                    cancelPrepared(prepared?.handle);
                    setPrepared(null);
                    setError('');
                    setStep(1);
                  }
            }
          >
            {bootstrapCommitted
              ? t('vault.recoveryRetryUnlock', 'Try again')
              : t('vault.recoveryStartOver', 'Start over')}
          </button>
        </div>
      )}
    </Modal>
  );
}
